import React from "react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { FileText, BookOpen, Settings } from "lucide-react";
import { TagInput } from "../../../components/CreateProject";
import TiptapEditor from "../../../components/ui/TiptapEditor";
import {
  useResourceProfilesQuery,
  useResourceProfileByIdQuery,
} from "../../../api/sandboxManagementApi";
import { ProjectDetailsStepProps } from "../types";

export const ProjectDetailsStep = React.memo<ProjectDetailsStepProps>(
  ({ formData, handleInputChange }) => {
    // Add resource profile hooks
    const { data: resourceProfiles, isLoading: resourceProfilesLoading } =
      useResourceProfilesQuery();

    const { data: resourceProfile } = useResourceProfileByIdQuery(
      formData.resource_profile_id,
      { enabled: !!formData.resource_profile_id },
    );

    return (
      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Basic Information</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Provide the basic information and requirements
            </p>

            <div className="space-y-4">
              {/* Project Title */}
              <div>
                <Label htmlFor="title">
                  Project Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter project title"
                  className="mt-1"
                />
              </div>

              {/* Project Description */}
              <div>
                <Label htmlFor="description">
                  Project Descriptionnn <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Enter project description"
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Difficulty Level */}
              <div>
                <Label>
                  Difficulty Level <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.difficulty_level}
                  onValueChange={(value) =>
                    handleInputChange("difficulty_level", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Project Tags */}
              <div>
                <Label>Project Tags</Label>
                <div className="mt-1">
                  <TagInput
                    tags={formData.tags}
                    setTags={(tags) => handleInputChange("tags", tags)}
                    placeholder="Add tags (e.g., machine learning, python, beginner)"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Metadata */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Project Metadata</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Configure sandbox environment settings for student access
            </p>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Resource Profile Select */}
                <div>
                  <Label htmlFor="resource_profile_id">
                    Select Resource Profile{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    key={`resource-profile-${formData.resource_profile_id}`}
                    value={formData.resource_profile_id || ""}
                    onValueChange={(value) =>
                      handleInputChange("resource_profile_id", value)
                    }
                  >
                    <SelectTrigger className="mt-1 bg-bits-grey-100">
                      <SelectValue placeholder="-- Select Resource Profile --" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {resourceProfilesLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading Resource Profiles...
                        </SelectItem>
                      ) : resourceProfiles?.profiles &&
                        resourceProfiles.profiles.length > 0 ? (
                        resourceProfiles.profiles.map((profile: any) => (
                          <SelectItem
                            key={profile.id}
                            value={String(profile.id)}
                          >
                            {profile.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-profiles" disabled>
                          No resource profiles available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Resource profile enables Jupyter sandbox access for students
                  </p>
                </div>

                {/* Sandbox Duration */}
                <div>
                  <Label htmlFor="sandbox_time_duration">
                    Sandbox Duration <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="sandbox_time_duration"
                    type="text"
                    value={formData.sandbox_time_duration || "01:00"}
                    onChange={(e) =>
                      handleInputChange("sandbox_time_duration", e.target.value)
                    }
                    placeholder="01:00"
                    pattern="[0-9]{2}:[0-5][0-9]"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Duration in HH:MM format (e.g., 01:00 for 1 hour, 02:30 for
                    2.5 hours)
                  </p>
                </div>
              </div>

              {/* Resource Profile Details Card */}
              {resourceProfile && (
                <div className="p-5 bg-yellow-50 border border-yellow-100 rounded-xl shadow-sm space-y-3">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {resourceProfile.name}
                  </h2>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                    <p>
                      <strong>Resource Type:</strong>{" "}
                      {resourceProfile.resource_type}
                    </p>
                    <p>
                      <strong>CPU Cores:</strong> {resourceProfile.cpu_cores}
                    </p>
                    <p>
                      <strong>RAM (GB):</strong> {resourceProfile.ram_gb}
                    </p>
                    <p>
                      <strong>Disk (GB):</strong> {resourceProfile.disk_gb}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Project Overview */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Project Overview</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Provide a comprehensive overview of the project
            </p>

            <div>
              <Label htmlFor="project_overview">Project Overview</Label>
              <div className="mt-1">
                <TiptapEditor
                  value={formData.project_overview}
                  onChange={(html) =>
                    handleInputChange("project_overview", html)
                  }
                  placeholder="Describe the project overview, goals, and what students will build..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Learning Objectives */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Learning Objectives</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Define what students will learn through this project
            </p>

            <div>
              <Label htmlFor="objectives">Learning Objectives</Label>
              <div className="mt-1">
                <TiptapEditor
                  value={formData.learning_objectives}
                  onChange={(html) =>
                    handleInputChange("learning_objectives", html)
                  }
                  placeholder="List the learning objectives..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prerequisites */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">
                Prerequisites & Requirements
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Specify the knowledge, skills, and tools students need before
              starting
            </p>

            <div>
              <Label htmlFor="prerequisites">Prerequisites</Label>
              <div className="mt-1">
                <TiptapEditor
                  value={formData.prerequisites}
                  onChange={(html) => handleInputChange("prerequisites", html)}
                  placeholder="List prerequisites and requirements..."
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  },
);

ProjectDetailsStep.displayName = "ProjectDetailsStep";

export default ProjectDetailsStep;
